import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ChevronLeft, ChevronRight, Check, Plus, Trash2, ShoppingCart, FileText, IndianRupee, MapPin, Save, Globe, FileSearch, Zap, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const h = React.createElement;

const PO_TYPES = [
  { value: "DOMESTIC", label: "Domestic PO", desc: "Purchase from Indian vendors", icon: ShoppingCart, color: "blue" },
  { value: "GLOBAL", label: "Global PO", desc: "Import from international vendors", icon: Globe, color: "violet" },
];
const PO_SUB_TYPES = [
  { value: "GENERAL", label: "General", desc: "Equipment, instruments, furniture" },
  { value: "SOFTWARE", label: "Software", desc: "Licenses, subscriptions, digital" },
  { value: "AMC", label: "AMC", desc: "Annual Maintenance Contracts" },
  { value: "SERVICE", label: "Service", desc: "Professional & consulting services" },
];
const STEPS = [
  { id: 0, title: "PO Type", icon: ShoppingCart, desc: "Type, category, indentor and fund" },
  { id: 1, title: "Vendor & Reference", icon: FileText, desc: "Vendor details, subject and reference" },
  { id: 2, title: "Items & Pricing", icon: IndianRupee, desc: "Line items with pricing breakdown" },
  { id: 3, title: "Terms & Delivery", icon: MapPin, desc: "Payment, warranty, delivery and summary" },
];
const emptyItem = () => ({ serial_no: 1, description: "", code: "", hsn_code: "", quantity: 1, unit: "Nos", unit_price: 0, amount: 0, gst_percentage: 18, freight_charges: 0, packaging_charges: 0, discount_amount: 0, sub_items: [] });
const emptySubItem = () => ({ description: "", quantity: 1, unit: "Nos", unit_price: 0 });
const GST_OPTIONS = [0, 5, 12, 18, 28];

function PurchaseOrderForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAuthHeader } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSourceModal, setShowSourceModal] = useState(true);
  const [prList, setPrList] = useState([]);
  const [prLoading, setPrLoading] = useState(false);
  const [prSearch, setPrSearch] = useState("");
  const [selectedPrId, setSelectedPrId] = useState("");

  // Edit / amend params (from query string)
  const editPoId = searchParams.get("edit");
  const amendPoId = searchParams.get("amend");
  const isEdit = !!editPoId;
  const isAmend = !!amendPoId;
  const isCreate = !isEdit && !isAmend;

  // Vendor master
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState("");
  const [showVendorList, setShowVendorList] = useState(false);
  const [vendorsLoaded, setVendorsLoaded] = useState(false);
  useEffect(() => {
    axios.get(API_URL + "/vendors", getAuthHeader())
      .then(r => { setVendors(Array.isArray(r.data) ? r.data : []); setVendorsLoaded(true); })
      .catch(() => { setVendors([]); setVendorsLoaded(true); });
  }, []);

  const [form, setForm] = useState({
    po_type: "", po_sub_type: "", po_source: "", requisition_id: "",
    vendor_details: { name: "", address: "", gst_number: "", pan_number: "", contact_person: "", email: "", phone: "" },
    subject: "", reference: "",
    items: [emptyItem()],
    discount: 0, gst_percentage: 18, gst_amount: 0, customs_charges: 0, total_amount: 0,
    payment_terms: "Payment after delivery, installation and acceptance within 30 days after submission of Invoice.",
    warranty_details: "1 year from the date of supply & successful installation.",
    delivery_terms: "", delivery_weeks: 4,
    prices_basis: "Inclusive all, FOR IIT Hyderabad",
    place_of_delivery: "IIT Hyderabad, Kandi, Sangareddy",
    ld_applicable: true, fund_type: "INSTITUTE", fund_allocation: [],
    category: "NON_CONSUMABLE",
    indentor_name: user?.name || "", indentor_department: user?.department || "",
  });

  // Edit / Amend hydration
  useEffect(() => {
    const targetId = editPoId || amendPoId;
    if (!targetId) return;
    setShowSourceModal(false);
    axios.get(API_URL + "/purchase-orders/" + targetId, getAuthHeader())
      .then(r => {
        const po = r.data;
        setForm(f => ({ ...f, ...po,
          // ensure shapes are correct
          vendor_details: po.vendor_details || f.vendor_details,
          items: (po.items && po.items.length) ? po.items : f.items,
          po_source: po.requisition_id ? "PR_BASED" : "DIRECT",
        }));
        setStep(0);
      })
      .catch(() => toast.error("Failed to load PO for " + (isEdit ? "edit" : "amendment")));
  }, [editPoId, amendPoId]);

  // Fund Types master (loaded dynamically)
  const [fundTypes, setFundTypes] = useState([
    { value: "INSTITUTE", label: "Institute Fund", is_core: true },
    { value: "PROJECT", label: "Project Fund", is_core: true },
  ]);
  useEffect(() => {
    axios.get(API_URL + "/fund-types-master", getAuthHeader())
      .then(r => { if (Array.isArray(r.data) && r.data.length) setFundTypes(r.data); })
      .catch(() => {});
  }, []);

  // Auto-handle URL param
  useEffect(() => {
    const prId = searchParams.get("from_pr");
    if (prId) {
      setShowSourceModal(false);
      populateFromPR(prId);
    }
  }, [searchParams]);

  const fetchPRs = async () => {
    setPrLoading(true);
    try { const r = await axios.get(API_URL + "/requisitions", getAuthHeader()); setPrList(r.data); }
    catch {}
    finally { setPrLoading(false); }
  };

  const populateFromPR = (prId) => {
    axios.get(API_URL + "/requisitions/" + prId, getAuthHeader()).then(r => {
      const pr = r.data;
      const prItems = (pr.items || []).map((item, idx) => ({
        serial_no: idx + 1, description: item.description || "", code: item.code || "",
        hsn_code: item.hsn_code || "", quantity: item.quantity || 1, unit: item.unit || "Nos",
        unit_price: item.estimated_cost || 0, amount: (item.quantity || 1) * (item.estimated_cost || 0),
      }));
      setForm(f => ({ ...f, requisition_id: pr.id, po_source: "PR_BASED", po_type: f.po_type || "DOMESTIC",
        subject: 'Supply of "' + (pr.item_description || "") + '" as per ' + (pr.requisition_number || ""),
        reference: pr.requisition_number || "", items: prItems.length > 0 ? prItems : f.items,
        fund_type: pr.fund_type || "INSTITUTE", category: pr.category || "NON_CONSUMABLE",
        indentor_name: pr.indentor_name || f.indentor_name, indentor_department: pr.department || f.indentor_department,
      }));
    }).catch(() => {});
  };

  const handleSourceSelect = (source) => {
    setForm(f => ({ ...f, po_source: source }));
    if (source === "DIRECT") { setShowSourceModal(false); }
    else { fetchPRs(); }
  };

  const confirmPRSelection = () => {
    if (!selectedPrId) { toast.error("Please select a Purchase Requisition"); return; }
    populateFromPR(selectedPrId);
    setShowSourceModal(false);
    toast.success("PR data loaded into PO form");
  };

  const set = useCallback((key, val) => setForm(f => ({ ...f, [key]: val })), []);
  const setVendor = useCallback((key, val) => setForm(f => ({ ...f, vendor_details: { ...f.vendor_details, [key]: val } })), []);
  // Recompute a line's "amount" = (qty × unit_price + Σ sub_items qty×price) − discount + freight + packaging.
  // Note: GST is per-item now and accumulated separately; not folded into the line "amount" here.
  const recomputeAmount = (it) => {
    const main = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0);
    const sub = (it.sub_items || []).reduce((s, si) => s + (parseFloat(si.unit_price) || 0) * (parseInt(si.quantity) || 0), 0);
    const charges = (parseFloat(it.freight_charges) || 0) + (parseFloat(it.packaging_charges) || 0);
    const discount = (parseFloat(it.discount_amount) || 0);
    return Math.max(0, main + sub + charges - discount);
  };
  const setItem = useCallback((idx, key, val) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: val };
      items[idx].amount = recomputeAmount(items[idx]);
      return { ...f, items };
    });
  }, []);
  const setSubItem = (idx, sIdx, key, val) => {
    setForm(f => {
      const items = [...f.items];
      const subs = [...(items[idx].sub_items || [])];
      subs[sIdx] = { ...subs[sIdx], [key]: val };
      items[idx] = { ...items[idx], sub_items: subs };
      items[idx].amount = recomputeAmount(items[idx]);
      return { ...f, items };
    });
  };
  const addSubItem = (idx) => setForm(f => {
    const items = [...f.items];
    const subs = [...(items[idx].sub_items || []), emptySubItem()];
    items[idx] = { ...items[idx], sub_items: subs };
    items[idx].amount = recomputeAmount(items[idx]);
    return { ...f, items };
  });
  const removeSubItem = (idx, sIdx) => setForm(f => {
    const items = [...f.items];
    const subs = (items[idx].sub_items || []).filter((_, i) => i !== sIdx);
    items[idx] = { ...items[idx], sub_items: subs };
    items[idx].amount = recomputeAmount(items[idx]);
    return { ...f, items };
  });
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem(), serial_no: f.items.length + 1 }] }));
  const removeItem = (idx) => { if (form.items.length <= 1) return; setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, serial_no: i + 1 })) })); };

  // Per-item GST is the new source of truth. The header `gst_percentage` is kept
  // as a read-only weighted-average display only.
  const subtotal = form.items.reduce((s, i) => s + (i.amount || 0), 0);
  const totalGst = form.items.reduce((s, i) => {
    const gstable = (parseFloat(i.unit_price) || 0) * (parseInt(i.quantity) || 0)
      + (i.sub_items || []).reduce((ss, si) => ss + (parseFloat(si.unit_price) || 0) * (parseInt(si.quantity) || 0), 0)
      - (parseFloat(i.discount_amount) || 0);
    return s + Math.max(0, gstable) * (parseFloat(i.gst_percentage) || 0) / 100;
  }, 0);
  const headerDiscount = (parseFloat(form.discount) || 0);
  const afterDiscount = Math.max(0, subtotal - headerDiscount);
  const gstAmt = Math.round(totalGst);
  const weightedAvgGst = subtotal > 0 ? Math.round((totalGst / subtotal) * 10000) / 100 : 0;
  const grandTotal = Math.round(afterDiscount + gstAmt + (parseFloat(form.customs_charges) || 0));

  const handleSubmit = async () => {
    const errs = {};
    if (!form.po_type) errs.po_type = "Select PO type";
    if (!form.po_sub_type) errs.po_sub_type = "Select category";
    if (!form.vendor_details.name) errs.vendor = "Vendor name required";
    if (!form.items.length || !form.items[0].description) errs.items = "At least one item required";
    if (grandTotal <= 0) errs.total = "Total must be > 0";
    if (Object.keys(errs).length) { setErrors(errs); Object.values(errs).forEach(e => toast.error(e)); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, gst_percentage: weightedAvgGst, gst_amount: gstAmt, total_amount: grandTotal, requisition_id: form.requisition_id || undefined, po_sub_type: form.po_sub_type };
      if (isEdit || isAmend) {
        const targetId = editPoId || amendPoId;
        await axios.put(API_URL + "/purchase-orders/" + targetId + "/amend", payload, getAuthHeader());
        toast.success("Purchase Order amended!");
      } else {
        await axios.post(API_URL + "/purchase-orders", payload, getAuthHeader());
        toast.success("Purchase Order created!");
      }
      navigate("/purchase-orders");
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) msg = detail.map(d => `${Array.isArray(d.loc) ? d.loc.slice(1).join(".") : "field"}: ${d.msg}`).join("; ");
      else msg = (typeof detail === "string" ? detail : null) || "Failed to save PO";
      toast.error(msg);
    }
    finally { setSubmitting(false); }
  };

  const canNext = () => { if (step === 0) return !!form.po_type && !!form.po_sub_type; if (step === 1) return !!form.vendor_details.name; if (step === 2) return form.items.length > 0 && form.items[0].description; return true; };

  // Live validation issues for the final submit step
  const poValidationIssues = (() => {
    const i = [];
    if (!form.po_type) i.push("Select PO Type (Domestic / Global) on Step 1.");
    if (!form.po_sub_type) i.push("Select PO Sub-Type / Category on Step 1.");
    if (!form.vendor_details.name) i.push("Enter Vendor Name on Step 2.");
    if (!form.items.length || !form.items[0].description) i.push("Add at least one Item with description on Step 3.");
    if (grandTotal <= 0) i.push("Total must be greater than zero \u2014 fill Quantity \u00D7 Unit Price on items.");
    return i;
  })();

  const renderActions = () => h("div", { className: "space-y-3" },
    // Inline validation banner ONLY on final step
    step === STEPS.length - 1 && poValidationIssues.length > 0 && h("div", {
      className: "p-4 rounded-xl border border-amber-300 bg-amber-50",
      "data-testid": "po-validation-summary"
    },
      h("p", { className: "text-sm font-semibold text-amber-900 mb-2" }, "\u26A0\uFE0F Please fix the following before saving (", poValidationIssues.length, "):"),
      h("ul", { className: "list-disc ml-5 space-y-1" }, ...poValidationIssues.map((m, k) => h("li", { key: k, className: "text-sm text-amber-800" }, m)))
    ),
    h("div", { className: "flex items-center justify-between" },
      h(Button, { variant: "outline", onClick: () => step > 0 ? setStep(step - 1) : navigate("/purchase-orders"), "data-testid": "prev-btn" }, h(ChevronLeft, { className: "w-4 h-4 mr-1" }), step === 0 ? "Cancel" : "Previous"),
      h("div", { className: "flex gap-2" },
        step < STEPS.length - 1
          ? h(Button, { onClick: () => setStep(step + 1), disabled: !canNext(), className: "bg-slate-900 hover:bg-slate-800", "data-testid": "next-btn" }, "Next ", h(ChevronRight, { className: "w-4 h-4 ml-1" }))
          : h(Button, { onClick: handleSubmit, disabled: submitting || poValidationIssues.length > 0, className: "bg-emerald-600 hover:bg-emerald-700", "data-testid": "submit-po-btn" }, h(Save, { className: "w-4 h-4 mr-2" }), submitting ? "Creating..." : (poValidationIssues.length > 0 ? `Fix ${poValidationIssues.length} issue${poValidationIssues.length > 1 ? "s" : ""} to submit` : "Create Purchase Order"))
      )
    )
  );
  const renderField = (label, content, req, span) => h("div", { className: span === 2 ? "md:col-span-2" : span === 3 ? "md:col-span-3" : "" }, h(Label, { className: "text-xs font-medium text-slate-600" }, label, req && h("span", { className: "text-rose-500 ml-0.5" }, "*")), h("div", { className: "mt-1.5" }, content));

  // ========== SOURCE MODAL ==========
  const renderSourceModal = () => {
    if (!showSourceModal || isEdit || isAmend) return null;
    const filteredPRs = prList.filter(p => !prSearch || (p.requisition_number || "").toLowerCase().includes(prSearch.toLowerCase()) || (p.item_description || "").toLowerCase().includes(prSearch.toLowerCase()));
    return h("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm", "data-testid": "po-source-modal" },
      h("div", { className: "bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" },
        h("div", { className: "p-6 border-b border-slate-100 bg-slate-50" },
          h("div", { className: "flex items-center justify-between" },
            h("div", null,
              h("h2", { className: "text-lg font-bold text-slate-900" }, "Create Purchase Order"),
              h("p", { className: "text-sm text-slate-500 mt-0.5" }, "How would you like to create this PO?")
            ),
            h("button", { onClick: () => navigate("/purchase-orders"), className: "p-1.5 rounded-lg hover:bg-slate-200 text-slate-400" }, h(X, { className: "w-5 h-5" }))
          )
        ),
        !form.po_source ? h("div", { className: "p-6 space-y-4" },
          h("button", { onClick: () => handleSourceSelect("PR_BASED"), className: "w-full p-5 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 text-left transition-all group", "data-testid": "source-pr" },
            h("div", { className: "flex items-center gap-4" },
              h("div", { className: "p-3 rounded-xl bg-emerald-50 group-hover:bg-emerald-100" }, h(FileSearch, { className: "w-6 h-6 text-emerald-600" })),
              h("div", null,
                h("p", { className: "font-semibold text-slate-900" }, "Reference an Approved Purchase Requisition"),
                h("p", { className: "text-sm text-slate-500 mt-0.5" }, "Select an approved PR \u2014 all line items, fund and committee details auto-fill into this PO")
              )
            )
          ),
          h("p", { className: "text-xs text-slate-400 px-1" }, "Note: per institute policy, Purchase Orders may only be created against an approved Purchase Requisition.")
        ) :
        // PR Selection view
        h("div", { className: "flex flex-col", style: { maxHeight: "calc(85vh - 88px)" } },
          h("div", { className: "p-4 border-b border-slate-100" },
            h("div", { className: "flex items-center gap-3" },
              h("button", { onClick: () => setForm(f => ({ ...f, po_source: "" })), className: "p-1 rounded hover:bg-slate-100" }, h(ChevronLeft, { className: "w-4 h-4" })),
              h("p", { className: "text-sm font-semibold text-slate-700" }, "Select Purchase Requisition"),
            ),
            h("div", { className: "mt-3 relative" },
              h(Input, { value: prSearch, onChange: e => setPrSearch(e.target.value), placeholder: "Search by PR number or description...", className: "pl-3" })
            )
          ),
          h("div", { className: "overflow-y-auto flex-1 p-2" },
            prLoading ? h("div", { className: "p-8 text-center text-slate-400" }, "Loading requisitions...") :
            filteredPRs.length > 0 ? filteredPRs.map(pr =>
              h("button", { key: pr.id, onClick: () => setSelectedPrId(pr.id),
                className: "w-full p-3 rounded-lg text-left mb-1 transition-all border " + (selectedPrId === pr.id ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-transparent hover:bg-slate-50")
              },
                h("div", { className: "flex items-center justify-between" },
                  h("div", { className: "min-w-0" },
                    h("p", { className: "font-mono font-semibold text-sm text-slate-900" }, pr.requisition_number),
                    h("p", { className: "text-xs text-slate-500 truncate mt-0.5" }, pr.item_description)
                  ),
                  h("div", { className: "text-right shrink-0 ml-4" },
                    h("p", { className: "font-semibold text-sm" }, "\u20B9" + (pr.estimated_value || 0).toLocaleString("en-IN")),
                    h("p", { className: "text-xs text-slate-400" }, pr.procurement_mode?.replace(/_/g, " ") || ""),
                    selectedPrId === pr.id && h(Check, { className: "w-4 h-4 text-emerald-600 ml-auto mt-1" })
                  )
                )
              )
            ) : h("p", { className: "p-8 text-center text-slate-400 text-sm" }, "No requisitions found")
          ),
          h("div", { className: "p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3" },
            h(Button, { variant: "outline", onClick: () => setForm(f => ({ ...f, po_source: "" })) }, "Back"),
            h(Button, { onClick: confirmPRSelection, disabled: !selectedPrId, className: "bg-emerald-600 hover:bg-emerald-700", "data-testid": "confirm-pr" }, h(Check, { className: "w-4 h-4 mr-2" }), "Use Selected PR")
          )
        )
      )
    );
  };

  // ========== STEPPER ==========
  const renderStepper = () => h("div", { className: "flex items-center gap-1 p-1 bg-slate-50 rounded-xl border border-slate-100" },
    STEPS.map((s, i) => { const isActive = step === i, isDone = step > i;
      return h("button", { key: s.id, onClick: () => setStep(i), className: "flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-all duration-200 " + (isActive ? "bg-white shadow-sm border border-slate-200" : isDone ? "opacity-80 hover:opacity-100" : "opacity-50") },
        h("div", { className: "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 " + (isDone ? "bg-emerald-500 text-white" : isActive ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500") }, isDone ? h(Check, { className: "w-3.5 h-3.5" }) : i + 1),
        h("div", { className: "hidden lg:block min-w-0" }, h("p", { className: "text-xs font-semibold truncate " + (isActive ? "text-slate-900" : "text-slate-500") }, s.title))); })
  );

  // ========== STEP 0: PO Type ==========
  const renderStep0 = () => h("div", { className: "space-y-6" },
    form.requisition_id && h("div", { className: "p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-3" },
      h(FileSearch, { className: "w-5 h-5 text-emerald-600 shrink-0" }),
      h("div", { className: "min-w-0" },
        h("p", { className: "text-sm font-semibold text-emerald-800" }, "Linked to PR: ", form.reference),
        h("p", { className: "text-xs text-emerald-600 truncate" }, form.subject)
      ),
      h("button", { onClick: () => { setShowSourceModal(true); setForm(f => ({ ...f, po_source: "", requisition_id: "", reference: "", subject: "", items: [emptyItem()] })); }, className: "text-xs text-emerald-700 underline shrink-0 ml-auto" }, "Change")
    ),
    h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Purchase Order Type"),
    h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
      PO_TYPES.map(t => h("button", { key: t.value, onClick: () => { set("po_type", t.value); set("po_sub_type", ""); }, "data-testid": "po-type-" + t.value.toLowerCase(),
        className: "p-5 rounded-xl border-2 text-left transition-all " + (form.po_type === t.value ? "border-" + t.color + "-600 bg-" + t.color + "-50 shadow-sm" : "border-slate-200 hover:border-slate-300") },
        h("div", { className: "flex items-center gap-3 mb-1" }, h(t.icon, { className: "w-5 h-5 " + (form.po_type === t.value ? "text-" + t.color + "-700" : "text-slate-400") }), h("p", { className: "font-semibold text-sm " + (form.po_type === t.value ? "text-slate-900" : "text-slate-600") }, t.label)),
        h("p", { className: "text-xs text-slate-400" }, t.desc)))
    ),
    form.po_type && h("div", { className: "space-y-3" },
      h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Category"),
      h("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-3" },
        PO_SUB_TYPES.map(st => h("button", { key: st.value, onClick: () => set("po_sub_type", st.value), "data-testid": "po-sub-" + st.value.toLowerCase(),
          className: "p-3 rounded-lg border-2 text-left transition-all " + (form.po_sub_type === st.value ? "border-slate-900 bg-slate-50 shadow-sm" : "border-slate-200 hover:border-slate-300") },
          h("p", { className: "font-semibold text-xs " + (form.po_sub_type === st.value ? "text-slate-900" : "text-slate-600") }, st.label),
          h("p", { className: "text-xs text-slate-400 mt-0.5" }, st.desc)))
      )
    ),
    h("div", { className: "space-y-3 mt-2" },
      h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Indentor & Fund"),
      h("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4" },
        renderField("Indentor Name", h(Input, { value: form.indentor_name, onChange: e => set("indentor_name", e.target.value) })),
        renderField("Department", h(Input, { value: form.indentor_department, onChange: e => set("indentor_department", e.target.value) })),
        renderField("Fund Type", h("select", { value: form.fund_type, onChange: e => set("fund_type", e.target.value), className: "w-full h-9 px-3 rounded-md border border-slate-200 text-sm bg-white", "data-testid": "po-fund-type" }, ...fundTypes.map(ft => h("option", { key: ft.value, value: ft.value }, ft.label)))),
        renderField("Category", h("select", { value: form.category, onChange: e => set("category", e.target.value), className: "w-full h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" }, h("option", { value: "NON_CONSUMABLE" }, "Non-Consumable"), h("option", { value: "CONSUMABLE" }, "Consumable"), h("option", { value: "SERVICES" }, "Services")))
      )
    )
  );

  // ========== STEP 1: Vendor ==========
  const filteredVendors = vendors.filter(v => !v.is_blacklisted && (
    !vendorSearch || (v.name || "").toLowerCase().includes(vendorSearch.toLowerCase())
      || (v.vendor_code || "").toLowerCase().includes(vendorSearch.toLowerCase())
      || (v.gst_number || "").toLowerCase().includes(vendorSearch.toLowerCase())
  ));

  const pickVendor = (v) => {
    setForm(f => ({ ...f, vendor_details: {
      name: v.name || "",
      address: [v.address, v.city, v.state, v.country, v.pincode].filter(Boolean).join(", "),
      gst_number: v.gst_number || "",
      pan_number: v.pan_number || "",
      contact_person: v.contact_person || "",
      email: v.email || "",
      phone: v.phone || "",
      vendor_id: v.id,
    }}));
    setVendorSearch(v.name || "");
    setShowVendorList(false);
  };

  const renderStep1 = () => h("div", { className: "space-y-6" },
    h("div", { className: "space-y-3" }, h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Vendor / Supplier"),
      // Vendor lookup combobox
      h("div", { className: "relative" },
        h(Label, { className: "text-xs font-medium text-slate-600 flex items-center gap-2" },
          h("span", null, "Search Vendor Master"),
          h("span", { className: vendorsLoaded ? (vendors.length > 0 ? "px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold" : "px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold") : "text-slate-400 text-[10px]" },
            vendorsLoaded ? (vendors.length + " vendors loaded") : "loading vendors\u2026"
          )
        ),
        h("div", { className: "relative mt-1.5" },
          h(Input, {
            value: vendorSearch || form.vendor_details.name,
            onChange: e => { setVendorSearch(e.target.value); setShowVendorList(true); },
            onFocus: () => setShowVendorList(true),
            placeholder: vendors.length === 0 ? "No vendors in master \u2014 fill manually below" : "Type vendor name, code or GST to search...",
            "data-testid": "vendor-search-input",
            className: "pr-9"
          }),
          form.vendor_details.name && h("button", { type: "button",
            onClick: () => { setForm(f => ({ ...f, vendor_details: { name:"",address:"",gst_number:"",pan_number:"",contact_person:"",email:"",phone:"" } })); setVendorSearch(""); },
            className: "absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded" }, h(X, { className: "w-4 h-4 text-slate-400" }))
        ),
        showVendorList && filteredVendors.length > 0 && h("div", {
          className: "absolute z-20 mt-1 left-0 right-0 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg",
          "data-testid": "vendor-dropdown"
        },
          filteredVendors.slice(0, 50).map(v => h("button", {
            key: v.id, type: "button", onClick: () => pickVendor(v),
            className: "w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0",
            "data-testid": "vendor-option-" + v.id
          },
            h("div", { className: "flex items-center justify-between gap-2" },
              h("div", { className: "min-w-0 flex-1" },
                h("p", { className: "text-sm font-semibold text-slate-900 truncate" }, v.name),
                h("p", { className: "text-xs text-slate-500" },
                  v.vendor_code, v.gst_number ? " \u2022 GST: " + v.gst_number : "",
                  v.city ? " \u2022 " + v.city : ""
                )
              ),
              h(Badge, { variant: "outline", className: "text-[10px] shrink-0" }, v.category || "DOMESTIC")
            )
          ))
        ),
        showVendorList && vendorSearch && filteredVendors.length === 0 && h("div", {
          className: "absolute z-20 mt-1 left-0 right-0 px-3 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-500 shadow-lg"
        },
          "No vendors match \"" + vendorSearch + "\". ",
          h("button", { type: "button", onClick: () => navigate("/vendors"), className: "text-blue-600 underline" }, "Add a new vendor"),
          " or fill the form manually below."
        )
      ),
      h("p", { className: "text-[11px] text-slate-400 italic" }, "Tip: pick from master to auto-fill all vendor fields, OR fill them manually below for one-off vendors."),
      h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100" },
        renderField("Vendor Name (M/s.)", h(Input, { value: form.vendor_details.name, onChange: e => setVendor("name", e.target.value), placeholder: "e.g. Spectris Technologies Pvt. Ltd.", "data-testid": "vendor-name" }), true),
        renderField("Contact Person", h(Input, { value: form.vendor_details.contact_person, onChange: e => setVendor("contact_person", e.target.value) })),
        renderField("Address", h(Textarea, { value: form.vendor_details.address, onChange: e => setVendor("address", e.target.value), rows: 2 }), false, 2),
        renderField("Phone", h(Input, { value: form.vendor_details.phone, onChange: e => setVendor("phone", e.target.value) })),
        renderField("Email", h(Input, { value: form.vendor_details.email, onChange: e => setVendor("email", e.target.value) })),
        renderField("GSTIN", h(Input, { value: form.vendor_details.gst_number, onChange: e => setVendor("gst_number", e.target.value) })),
        renderField("PAN", h(Input, { value: form.vendor_details.pan_number, onChange: e => setVendor("pan_number", e.target.value) }))
      )
    ),
    h("div", { className: "space-y-3" }, h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Subject & Reference"),
      h("div", { className: "grid grid-cols-1 gap-4" },
        renderField("Subject", h(Textarea, { value: form.subject, onChange: e => set("subject", e.target.value), rows: 2, placeholder: 'Supply of "Item description" - Reg.' })),
        renderField("Reference", h(Textarea, { value: form.reference, onChange: e => set("reference", e.target.value), rows: 2, placeholder: "Quotation No / PR Reference" }))
      )
    )
  );

  // ========== STEP 2: Items (Table format) ==========
  const renderStep2 = () => h("div", { className: "space-y-4" },
    h("div", { className: "flex items-center justify-between" },
      h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider" }, "Order Items"),
      h(Button, { variant: "outline", size: "sm", onClick: addItem, "data-testid": "add-item-btn" }, h(Plus, { className: "w-3.5 h-3.5 mr-1" }), "Add Item")
    ),
    // Items Table — per-item GST/freight/packaging/discount + optional sub-items
    h("div", { className: "border rounded-xl overflow-x-auto" },
      h(Table, { className: "min-w-[1400px]" },
        h(TableHeader, null, h(TableRow, { className: "bg-slate-900 text-white" },
          h(TableHead, { className: "text-white font-semibold w-10" }, "#"),
          h(TableHead, { className: "text-white font-semibold min-w-[220px]" }, "Description"),
          h(TableHead, { className: "text-white font-semibold w-24" }, "HSN"),
          h(TableHead, { className: "text-white font-semibold w-16 text-center" }, "Qty"),
          h(TableHead, { className: "text-white font-semibold w-20" }, "Unit"),
          h(TableHead, { className: "text-white font-semibold w-24 text-right" }, "Unit Price"),
          h(TableHead, { className: "text-white font-semibold w-20 text-center" }, "GST %"),
          h(TableHead, { className: "text-white font-semibold w-24 text-right" }, "Freight"),
          h(TableHead, { className: "text-white font-semibold w-24 text-right" }, "Packaging"),
          h(TableHead, { className: "text-white font-semibold w-24 text-right" }, "Discount"),
          h(TableHead, { className: "text-white font-semibold w-28 text-right" }, "Amount"),
          h(TableHead, { className: "text-white w-10" })
        )),
        h(TableBody, null,
          form.items.flatMap((item, idx) => [
            h(TableRow, { key: "row-" + idx, className: idx % 2 === 0 ? "bg-white" : "bg-slate-50/50" },
              h(TableCell, { className: "font-mono text-xs text-slate-500 text-center align-top pt-3" }, idx + 1),
              h(TableCell, { className: "align-top" },
                h(Input, { value: item.description, onChange: e => setItem(idx, "description", e.target.value), placeholder: "Item description", className: "text-sm h-8 border-slate-200", "data-testid": "item-desc-" + idx }),
                h("button", {
                  type: "button",
                  onClick: () => addSubItem(idx),
                  className: "mt-1 text-[11px] text-emerald-700 hover:text-emerald-900 hover:underline",
                  "data-testid": "add-sub-item-" + idx
                }, "+ Add sub-item")
              ),
              h(TableCell, { className: "align-top" }, h(Input, { value: item.hsn_code, onChange: e => setItem(idx, "hsn_code", e.target.value), placeholder: "HSN", className: "text-sm h-8 w-24 border-slate-200" })),
              h(TableCell, { className: "align-top" }, h(Input, { type: "number", min: 1, value: item.quantity, onChange: e => setItem(idx, "quantity", parseInt(e.target.value) || 1), className: "text-sm h-8 w-16 text-center border-slate-200" })),
              h(TableCell, { className: "align-top" }, h("select", { value: item.unit, onChange: e => setItem(idx, "unit", e.target.value), className: "h-8 px-2 rounded border border-slate-200 text-xs bg-white w-full" }, ["Nos", "Kg", "Ltr", "Mtr", "Set", "Box", "Lot"].map(u => h("option", { key: u, value: u }, u)))),
              h(TableCell, { className: "text-right align-top" }, h(Input, { type: "number", value: item.unit_price || "", onChange: e => setItem(idx, "unit_price", parseFloat(e.target.value) || 0), className: "text-sm h-8 w-24 text-right border-slate-200", "data-testid": "unit-price-" + idx })),
              h(TableCell, { className: "align-top" }, h("select", {
                value: item.gst_percentage != null ? String(item.gst_percentage) : "18",
                onChange: e => setItem(idx, "gst_percentage", parseFloat(e.target.value)),
                className: "h-8 px-2 rounded border border-slate-200 text-xs bg-white w-full",
                "data-testid": "item-gst-" + idx
              }, GST_OPTIONS.map(g => h("option", { key: g, value: g }, g + "%")))),
              h(TableCell, { className: "text-right align-top" }, h(Input, { type: "number", value: item.freight_charges || "", onChange: e => setItem(idx, "freight_charges", parseFloat(e.target.value) || 0), className: "text-sm h-8 w-24 text-right border-slate-200", "data-testid": "freight-" + idx })),
              h(TableCell, { className: "text-right align-top" }, h(Input, { type: "number", value: item.packaging_charges || "", onChange: e => setItem(idx, "packaging_charges", parseFloat(e.target.value) || 0), className: "text-sm h-8 w-24 text-right border-slate-200", "data-testid": "packaging-" + idx })),
              h(TableCell, { className: "text-right align-top" }, h(Input, { type: "number", value: item.discount_amount || "", onChange: e => setItem(idx, "discount_amount", parseFloat(e.target.value) || 0), className: "text-sm h-8 w-24 text-right border-slate-200", "data-testid": "item-discount-" + idx })),
              h(TableCell, { className: "text-right font-semibold text-emerald-700 text-sm align-top pt-3" }, "\u20B9" + (item.amount || 0).toLocaleString("en-IN")),
              h(TableCell, { className: "align-top" }, form.items.length > 1 && h("button", { onClick: () => removeItem(idx), className: "p-1 rounded hover:bg-rose-50 text-rose-400 hover:text-rose-600" }, h(Trash2, { className: "w-3.5 h-3.5" })))
            ),
            // Sub-items rendered as indented rows under their parent
            ...(item.sub_items || []).map((si, sIdx) => h(TableRow, {
              key: "sub-" + idx + "-" + sIdx,
              className: "bg-emerald-50/30",
              "data-testid": "sub-item-row-" + idx + "-" + sIdx
            },
              h(TableCell, { className: "text-[11px] text-slate-400 text-right pr-1" }, "\u21B3" + (sIdx + 1)),
              h(TableCell, null, h(Input, { value: si.description, onChange: e => setSubItem(idx, sIdx, "description", e.target.value), placeholder: "Sub-item description", className: "text-xs h-7 border-slate-200" })),
              h(TableCell, null),
              h(TableCell, null, h(Input, { type: "number", min: 1, value: si.quantity, onChange: e => setSubItem(idx, sIdx, "quantity", parseInt(e.target.value) || 1), className: "text-xs h-7 w-16 text-center border-slate-200" })),
              h(TableCell, null, h("select", { value: si.unit, onChange: e => setSubItem(idx, sIdx, "unit", e.target.value), className: "h-7 px-2 rounded border border-slate-200 text-xs bg-white w-full" }, ["Nos", "Kg", "Ltr", "Mtr", "Set", "Box", "Lot"].map(u => h("option", { key: u, value: u }, u)))),
              h(TableCell, { className: "text-right" }, h(Input, { type: "number", value: si.unit_price || "", onChange: e => setSubItem(idx, sIdx, "unit_price", parseFloat(e.target.value) || 0), className: "text-xs h-7 w-24 text-right border-slate-200" })),
              h(TableCell, { colSpan: 4, className: "text-[10px] text-slate-400 italic" }, "inherits parent GST/charges"),
              h(TableCell, { className: "text-right text-xs text-slate-600" }, "\u20B9" + ((si.unit_price || 0) * (si.quantity || 0)).toLocaleString("en-IN")),
              h(TableCell, null, h("button", { onClick: () => removeSubItem(idx, sIdx), className: "p-1 rounded hover:bg-rose-50 text-rose-400 hover:text-rose-600" }, h(Trash2, { className: "w-3 h-3" })))
            ))
          ]),
          // Totals row
          h(TableRow, { key: "subtotal-row", className: "border-t-2 border-slate-200 bg-slate-50" },
            h(TableCell, { colSpan: 10, className: "text-right text-sm font-medium text-slate-600" }, "Subtotal"),
            h(TableCell, { className: "text-right font-semibold" }, "\u20B9" + subtotal.toLocaleString("en-IN")),
            h(TableCell, null)
          )
        )
      )
    ),
    // Header-level Discount, customs (Global only), GST summary read-only
    h("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200" },
      h("div", null, h(Label, { className: "text-xs text-slate-500" }, "(-) Header Discount"), h(Input, { type: "number", value: form.discount || "", onChange: e => set("discount", parseFloat(e.target.value) || 0), className: "mt-1 h-8 text-sm" })),
      h("div", null,
        h(Label, { className: "text-xs text-slate-500" }, "Weighted GST %"),
        h("p", { className: "mt-2 font-semibold text-sm text-slate-700", "data-testid": "po-weighted-gst" }, weightedAvgGst + "%"),
        h("p", { className: "text-[10px] text-slate-400" }, "Computed from line items")
      ),
      form.po_type === "GLOBAL" && h("div", null, h(Label, { className: "text-xs text-slate-500" }, "Customs / Duties"), h(Input, { type: "number", value: form.customs_charges || "", onChange: e => set("customs_charges", parseFloat(e.target.value) || 0), className: "mt-1 h-8 text-sm" })),
      h("div", { className: "flex flex-col justify-end" }, h(Label, { className: "text-xs text-slate-500" }, "GST Amount"), h("p", { className: "font-semibold text-sm mt-1" }, "\u20B9" + gstAmt.toLocaleString("en-IN")))
    ),
    h("div", { className: "flex justify-end p-4 bg-slate-900 rounded-xl text-white" },
      h("div", { className: "text-right space-y-0.5" },
        subtotal !== afterDiscount && h("div", { className: "flex justify-between gap-8 text-xs text-slate-400" }, h("span", null, "After Discount"), h("span", null, "\u20B9" + afterDiscount.toLocaleString("en-IN"))),
        h("div", { className: "flex justify-between gap-8 text-xs text-slate-400" }, h("span", null, "GST (per-item)"), h("span", null, "+\u20B9" + gstAmt.toLocaleString("en-IN"))),
        h("div", { className: "flex justify-between gap-8 pt-2 border-t border-slate-700 mt-1" }, h("span", { className: "text-sm font-medium" }, "TOTAL"), h("span", { className: "text-xl font-bold", "data-testid": "po-total" }, "\u20B9" + grandTotal.toLocaleString("en-IN")))
      )
    )
  );

  // ========== STEP 3: Terms ==========
  const renderStep3 = () => h("div", { className: "space-y-6" },
    h("div", { className: "space-y-3" }, h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Pricing & Delivery"),
      h("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4" },
        renderField("Prices Basis", h(Input, { value: form.prices_basis, onChange: e => set("prices_basis", e.target.value) })),
        renderField("Place of Delivery", h(Input, { value: form.place_of_delivery, onChange: e => set("place_of_delivery", e.target.value) })),
        renderField("Delivery (Weeks)", h(Input, {
          type: "number", min: 0,
          value: form.delivery_weeks || "",
          onChange: e => {
            const w = parseInt(e.target.value);
            const safeW = Number.isFinite(w) ? w : null;
            setForm(f => ({ ...f, delivery_weeks: safeW, delivery_days: safeW != null ? safeW * 7 : null }));
          },
          "data-testid": "delivery-weeks"
        })),
        renderField("Delivery (Days)", h(Input, {
          type: "number", min: 0,
          value: form.delivery_days || "",
          onChange: e => {
            const d = parseInt(e.target.value);
            const safeD = Number.isFinite(d) ? d : null;
            setForm(f => ({ ...f, delivery_days: safeD, delivery_weeks: safeD != null ? Math.round(safeD / 7) : null }));
          },
          "data-testid": "delivery-days"
        }))
      )),
    h("div", { className: "space-y-3" }, h("h3", { className: "text-sm font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-100 pb-2" }, "Terms & Conditions"),
      h("div", { className: "grid grid-cols-1 gap-4" },
        renderField("Payment Terms", h(Textarea, { value: form.payment_terms, onChange: e => set("payment_terms", e.target.value), rows: 2 })),
        renderField("Warranty", h(Textarea, { value: form.warranty_details, onChange: e => set("warranty_details", e.target.value), rows: 2 })),
        renderField("Delivery Terms", h(Textarea, { value: form.delivery_terms, onChange: e => set("delivery_terms", e.target.value), rows: 2 }))
      )),
    h("div", { className: "p-5 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl text-white space-y-3" },
      h("h3", { className: "font-semibold text-sm uppercase tracking-wider text-slate-300" }, "Order Summary"),
      h("div", { className: "grid grid-cols-2 md:grid-cols-5 gap-4 text-sm" },
        h("div", null, h("p", { className: "text-slate-400 text-xs" }, "Source"), h("p", { className: "font-medium" }, form.po_source === "PR_BASED" ? "PR: " + form.reference : "Direct")),
        h("div", null, h("p", { className: "text-slate-400 text-xs" }, "Type"), h("p", { className: "font-medium" }, (PO_TYPES.find(t => t.value === form.po_type)?.label || "") + " / " + (form.po_sub_type || ""))),
        h("div", null, h("p", { className: "text-slate-400 text-xs" }, "Items"), h("p", { className: "font-medium" }, form.items.length)),
        h("div", null, h("p", { className: "text-slate-400 text-xs" }, "Fund"), h("p", { className: "font-medium" }, form.fund_type)),
        h("div", null, h("p", { className: "text-slate-400 text-xs" }, "Total"), h("p", { className: "font-bold text-lg text-emerald-400" }, "\u20B9" + grandTotal.toLocaleString("en-IN")))
      ),
      form.vendor_details.name && h("p", { className: "text-xs text-slate-400" }, "Vendor: ", h("span", { className: "text-white" }, form.vendor_details.name))
    )
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3];
  const StepIcon = STEPS[step].icon;

  return h("div", { className: "max-w-5xl mx-auto space-y-6", "data-testid": "po-form-page" },
    renderSourceModal(),
    h("div", { className: "flex items-center gap-4" },
      h(Button, { variant: "outline", size: "sm", onClick: () => navigate("/purchase-orders"), "data-testid": "back-btn" }, h(ChevronLeft, { className: "w-4 h-4 mr-1" }), " Back"),
      h("div", null,
        h("h1", { className: "text-xl font-bold text-slate-900" },
          (isEdit || isAmend) ? "Amend Purchase Order" : "Create Purchase Order"
        ),
        h("p", { className: "text-xs text-slate-500" }, form.po_type ? (PO_TYPES.find(t => t.value === form.po_type)?.label || "") + (form.po_sub_type ? " / " + form.po_sub_type : "") : "Select PO type to begin")
      )
    ),
    renderStepper(),
    h(Card, { className: "border-slate-200" },
      h(CardHeader, { className: "border-b border-slate-100 bg-slate-50/50 pb-4" },
        h("div", { className: "flex items-center gap-3" },
          h("div", { className: "p-2 rounded-lg bg-slate-900 text-white" }, h(StepIcon, { className: "w-4 h-4" })),
          h("div", null, h(CardTitle, { className: "text-base" }, STEPS[step].title), h("p", { className: "text-xs text-slate-500 mt-0.5" }, STEPS[step].desc))
        )
      ),
      h(CardContent, { className: "p-6" }, stepRenderers[step]())
    ),
    renderActions()
  );
}

export default PurchaseOrderForm;
